import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageTitle } from '../lib/usePageTitle';

export function ResetPassword() {
    const navigate = useNavigate();
    usePageTitle('Reset Password');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        // Check if we have the access token from the URL fragment (Supabase adds it there)
        // However, Supabase auth listener handles session recovery automatically if url is correct.
        // We will just wait for user to enter password.
    }, []);

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            setIsLoading(false);
            return;
        }

        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) throw error;
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to update password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            {/* Left Side - Visual */}
            <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1631541909061-71e349d1f241?q=80&w=1974&auto=format&fit=crop')] opacity-20 bg-cover bg-center" />
                <div className="relative z-10 p-12 text-white max-w-lg">
                    <div className="w-16 h-16 bg-brand rounded-2xl flex items-center justify-center mb-8 shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)]">
                        <Box size={32} className="text-white" />
                    </div>
                    <h1 className="text-5xl font-bold mb-6 tracking-tight">Secure Your Account</h1>
                    <p className="text-xl text-slate-400 font-light leading-relaxed">
                        Create a new, strong password to protect your designs and orders.
                    </p>
                </div>
            </div>

            {/* Right Side - Reset Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center lg:text-left">
                        <h2 className="text-3xl font-bold text-slate-900">Set New Password</h2>
                        <p className="text-slate-500 mt-2">Enter your new password below.</p>
                    </div>

                    <div className="space-y-4">
                        {error && (
                            <div className="p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 text-sm">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        {success ? (
                            <div className="p-6 bg-green-50 text-green-700 rounded-xl flex flex-col items-center gap-4 text-center">
                                <CheckCircle2 size={48} className="text-green-500" />
                                <div>
                                    <h3 className="font-bold text-lg">Password Updated!</h3>
                                    <p className="text-sm mt-1">Redirecting you to login...</p>
                                </div>
                            </div>
                        ) : (
                            <form className="space-y-4" onSubmit={handleResetPassword}>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">New Password</label>
                                    <div className="relative">
                                        <input
                                            required
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            maxLength={128}
                                            className="w-full p-3 pr-12 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Confirm Password</label>
                                    <div className="relative">
                                        <input
                                            required
                                            type={showPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="••••••••"
                                            maxLength={128}
                                            className="w-full p-3 pr-12 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </div>
                                <button
                                    disabled={isLoading}
                                    className="w-full bg-slate-900 text-white font-bold p-4 rounded-xl hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? <Loader2 className="animate-spin" /> : 'Update Password'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
